(ns accounts.manage-accounts
  (:require
    [reagent.core :as r]
    [reagent.format :as fmt]
    [very.re-frame.core :as rf]
    [css.core
     :refer [get-theme]
     :refer-macros [defstyled]]
    [css.form :as form]
    [css.icons :as icon]
    [css.vars :as vars]
    [cljss.core :as cljss]
    [cljs.spec.alpha :as s]
    [accounts.specs :as sp]
    [accounts.add-account :refer [generic-account-form account-type-form]]
    [accounts.common :as common]))

(defstyled name-and-type-s :div
  {:display "flex"
   :flex-direction "column"})

(defstyled account-bold-s :span
  {:color vars/gray
   :font-weight 700})

(defstyled account-light-s :span
  {:color "rgb(176,176,176)"
   :padding-top "0.4rem"})

(defstyled divider-s :span
  {:padding "0 .5rem"})

(defstyled overflow-container-s :div
  {:overflow "auto"})

(defstyled account-list-s :table
  {:border-collapse "collapse"
   :font-size "0.87rem"
   :table-layout "fixed"
   :width "100%"})

(defstyled th-s :th
  {:border-bottom "1px solid rgb(239,239,239)"
   :font-weight 600
   :padding "2rem 0 1rem"
   :text-align "left"})

(defstyled td-s :td
  {:border-bottom "1px solid rgb(239,239,239)"
   :padding "1rem 0"
   :vertical-align "middle"
   :font-size "14px"})

(defstyled no-accounts-s :div
  {:padding "2rem 1rem 1rem"
   :color vars/gray})

(defstyled mini-button :button
  {:background (with-meta #(if (= :dark %)
                             "rgb(61,61,61)"
                             "white") :theme)
   :border (with-meta #(if (= :dark %)
                         "none"
                         "1px solid rgb(61,61,61)") :theme)
   :color (with-meta #(if (= :dark %)
                        "white"
                        "rgb(61,61,61)") :theme)
   :cursor "pointer"
   :grid-row (with-meta identity :grid-row)
   :grid-column (with-meta identity :grid-column)
   :height "2rem"
   :margin (with-meta identity :margin)
   :padding "0 1rem"})

(defstyled delete-confirm-s :div
  {:display "grid"
   :grid-template-rows "min-content 1fr"
   :grid-template-columns "2fr 1fr 1fr"
   :grid-column-gap ".5rem"})

(defstyled delete-s :label
  {:color "rgb(202,202,202)"
   :grid-column "1"
   :grid-row "1"})

(defstyled delete-span-s :span
  {:align-self "center"
   :grid-column "1"
   :grid-row "2"})

(defn delete-confirm [confirm cancel]
  [delete-confirm-s
   [delete-s "Delete"]
   [delete-span-s "Are you sure?"]
   [mini-button {:theme :light
                 :on-click cancel
                 :grid-row "2"
                 :grid-column "2"} "Cancel"]
   [mini-button {:theme :dark
                 :on-click confirm
                 :grid-row "2"
                 :grid-column "3"} "Yes"]])

(defn manual-account []
  #_#{:list :confirm :deleting}
  (let [state (r/atom :list)]
    (fn [acc]
      [:tr
       [td-s
        [name-and-type-s
         [account-bold-s (:name acc)]
         [account-light-s (:type acc)]]]
       [td-s (fmt/currency-format (:market-value acc))]
       [td-s
        (condp = @state
          :confirm [delete-confirm #(do
                                      (reset! state :deleting)
                                      (rf/>evt [:delete-manual-account acc])) #(reset! state :list)]
          :deleting [:span "Deleting..."]
          :list [:div
                 [form/naked-button-s {:on-click #(rf/>evt [:edit-account acc])
                                       :display "inline-block"
                                       :color vars/gray} "Edit"]
                 [divider-s "|"]
                 [form/naked-button-s {:on-click #(reset! state :confirm)
                                       :display "inline-block"
                                       :color vars/gray} "Delete"]])]])))

(defstyled status-description :span
  ;; https://docs.quovo.com/data-dictionary/#connection-statuses
  {:color (with-meta #(condp = %
                        "incorrect_credentials" vars/red
                        "challenges" vars/red
                        "user_config" vars/red
                        "resync" vars/orange
                        "postponed" (get-theme :primary)
                        "maintenance" vars/orange
                        "institution_unavailable" vars/orange
                        "good" vars/green
                        (get-theme :primary)) :status)})

(defn- disconnect []
  (let [disconnecting? (r/atom false)]
    (fn [conn]
      [form/naked-button-s {:disabled @disconnecting?
                            :display "inline-block"
                            :color vars/gray
                            :on-click #(do (reset! disconnecting? true)
                                        (rf/>evt [:disconnect-account conn]))}
       (if @disconnecting? "Disconnecting..." "Disconnect")])))

(defstyled account-controls-s :div
  {:display "flex"})

(defn- fixable [conn]
  [account-controls-s
   [form/naked-button-s {:on-click #(rf/>evt [:fix-quovo-connection conn])
                         :color vars/gray} "Update"]
   [divider-s "|"]
   [disconnect conn]])

(defn connected-account [conn]
  #_#{}
  (let [state (r/atom nil)]
    (fn [conn]
      [:tr
       [td-s (:institution-name conn)]
       [td-s [status-description {:status (:status conn)} (:status-description conn)]]
       [td-s
        [:div
         (condp = (:status conn)
           "incorrect_credentials" [fixable conn]
           "challenges" [fixable conn]
           "user_config" [fixable conn]
           "resync" [fixable conn]
           "no_accounts" [disconnect conn]
           "postponed" [disconnect conn]
           "maintenance" [disconnect conn]
           "institution_unavailable" [disconnect conn]
           "good" [disconnect conn]
           [disconnect conn])]]])))

(defn connected-account-list []
  (let [conns (rf/subscribe [:quovo-connections])]
    (fn []
      (let [[tag v] (s/conform ::sp/quovo-connections @conns)]
        (condp = tag
          :connections
          (if (empty? @conns)
            [no-accounts-s "You have not connected any accounts"]
            [overflow-container-s
             [account-list-s
              [:thead
               [:tr
                [th-s]
                [th-s "Connection Status"]
                [th-s "Actions"]]]
              (into [:tbody]
                    (for [acc @conns]
                      [connected-account acc]))]])

          :none
          [no-accounts-s
           [:ul [:li "We are having trouble fetching your connected accounts. Please try again later."]]]

          :error
          [no-accounts-s
           [:span "There was an issue fetching your connected accounts. Please try again later."]])))))

(defn manual-account-list []
  (let [manual (rf/subscribe [:manual-accounts])]
    (fn []
      (if (empty? @manual)
        [no-accounts-s "You have not manually added any accounts"]
        [overflow-container-s
         [account-list-s
          ;[:colgroup
          ;[:col {:span "1"}]
          ;[:col {:span "1"}]
          ;[:col {:span "1" :width "35%"}]]
          [:thead
           [:tr
            [th-s]
            [th-s "Balance/market value"]
            [th-s "Actions"]]]
          (into [:tbody]
                (for [acc @manual]
                  [manual-account acc]))]]))))

(defstyled modal-header-s :header
  {:border-bottom "1px solid rgb(218,219,218)"
   :display "flex"
   :justify-content "flex-end"
   :padding-bottom "1rem"})

(defstyled cancel-i-s :i
  {:cursor "pointer"
   :width "2rem"
   :height "2rem"
   :background "url(/assets/icons/close.svg) no-repeat center"
   :background-size "2rem"})

(defn modal-header []
  [modal-header-s
   [cancel-i-s {:on-click #(rf/>evt [:abort-manage-accounts])}]])

(defstyled accounts-modal-s :div
  {:display "grid"
   :grid-template-rows "min-content min-content max-content min-content"
   :overflow "auto"
   :height "60vh"
   :padding "1rem"
   :width "65vw"
   ::cljss/media {[[:max-width "768px"]]
                  {:width "100vw"
                   :height "100vh"}}})

(defstyled tabs-s :div
  {:display "grid"
   :grid-template-columns "1fr 1fr"})

(defstyled tab-s :h1
  {:border-top "1px solid rgb(239,239,239)"
   :border-bottom (with-meta #(if (true? %) (str "3px solid " (get-theme :primary)) "3px solid rgb(239,239,239)") :active?)
   :color (with-meta #(if (true? %) (get-theme :primary)  "rgb(182,182,182)") :active?)
   :cursor "pointer"
   :font-size "1.2rem"
   :padding "3rem 0 2rem 0"
   :text-align "center"})

(defn modal-footer []
  [form/button-row-s {:margin "4rem 0 0"}
   [form/button {:on-click #(rf/>evt [:add-account])} "Add new account"]])

(defn ^:export manage-accounts-modal []
  #_{:state #{:connected
              :manual}}
  (let [state (r/atom {:state :connected})]
    (fn []
      [accounts-modal-s
       [modal-header]
       [tabs-s
        [tab-s {:on-click #(swap! state assoc :state :connected)
                :active? (= :connected (:state @state))} "Connected financial institutions"]
        [tab-s {:on-click #(swap! state assoc :state :manual)
                :active? (= :manual (:state @state))} "Manually added accounts"]]
       (condp = (:state @state)
         :connected [connected-account-list]
         :manual [manual-account-list])
       [modal-footer]])))

(defstyled cancel-s :i
  {:cursor "pointer"
   :font-size "2rem"})

(defn edit-account-form [{:keys [fields on-submit default-values]}]
  [common/modal-with-header {:title "Edit account"
                             :on-cancel #(rf/>evt [:abort-manage-accounts])}
   [generic-account-form {:fields fields
                          :default-values default-values
                          :on-submit on-submit
                          :button "Done"}]])

(defn ^:export edit-account-modal []
  (let [aue (rf/subscribe [:account-under-edit])
        on-submit #(rf/>evt [:edit-manual-account %])]
    (fn []
      [accounts-modal-s
       [account-type-form {:form edit-account-form
                           :type (:type @aue)
                           :default-values @aue
                           :on-submit on-submit}]])))
